import { useEffect, useState, useCallback } from "react";
import { listCalculations, deleteCalculation } from "../lib/api";
import { Trash2, FileText, Droplets, Cpu } from "lucide-react";
import { toast } from "sonner";

export default function HistoryPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await listCalculations()); } catch (e) { /* keep empty list on error */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function remove(id) {
    if (!window.confirm("Delete this calculation?")) return;
    try { await deleteCalculation(id); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Could not delete"); }
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] bg-grid-dark">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="inline-block bg-[#F26522] text-black px-3 py-1 font-mono text-[11px] font-black tracking-[0.2em] uppercase mb-3">
            Saved Calculations
          </div>
          <h1 className="font-heading text-5xl sm:text-6xl font-black uppercase tracking-tight text-white leading-[0.95]">History</h1>
        </div>

        {loading && <div className="text-white/60 font-mono text-sm">Loading…</div>}

        {!loading && rows.length === 0 && (
          <div className="bg-[#111111] border-2 border-dashed border-white/20 p-12 text-center" data-testid="empty-state">
            <FileText className="w-12 h-12 text-white/30 mx-auto mb-4" />
            <div className="font-heading text-xl font-black uppercase text-white mb-2">No saved calculations</div>
            <p className="text-sm text-white/60">Complete a dosing calc and tap <span className="font-mono text-[#F26522]">SAVE</span> on the results screen.</p>
          </div>
        )}

        <div className="space-y-3">
          {rows.map(r => (
            <div key={r.id} className="bg-[#111111] border-2 border-white/15 p-5 hover:border-[#F26522] transition-colors" data-testid={`history-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-lg font-black uppercase text-white truncate">{r.project_name || "Untitled Project"}</div>
                  <div className="font-mono text-[10px] tracking-wide uppercase text-[#F26522] mt-1 truncate">
                    {r.technician || "Anonymous"} · {new Date(r.created_at).toLocaleDateString()}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => remove(r.id)}
                  data-testid={`btn-delete-${r.id}`}
                  className="w-10 h-10 border-2 border-white/15 hover:border-[#F26522] hover:text-[#F26522] text-white/60 flex items-center justify-center transition-colors shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="bg-black border border-white/10 p-3">
                  <div className="flex items-center gap-1 text-[#F26522] mb-1">
                    <Droplets className="w-3.5 h-3.5" />
                    <div className="font-mono text-[9px] tracking-widest uppercase text-white/50">Dose</div>
                  </div>
                  <div className="font-mono text-xl font-medium text-white">{r.result?.dose_ppm ?? "—"} <span className="text-xs text-white/50">ppm</span></div>
                </div>
                <div className="bg-black border border-white/10 p-3">
                  <div className="flex items-center gap-1 text-[#F26522] mb-1">
                    <Cpu className="w-3.5 h-3.5" />
                    <div className="font-mono text-[9px] tracking-widest uppercase text-white/50">Pump</div>
                  </div>
                  <div className="font-mono text-xl font-medium text-white truncate">{r.result?.pump?.model ?? "—"}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
